/**
 * スワイプ風モーションクリップ (縦長キャプチャ静止画 → スワイプ動画素材) のパラメータ定義。
 * image-generator の docs/features/swipe-motion-clip-plan.md §5.2 / §5.5 / §9。
 *
 * **正本はここ (video-editor)**。image-generator の frontend/src/features/swipeClip/ 配下は
 * image-generator の scripts/sync-swipe-clip.mjs による**無改変コピー** (ハッシュ照合テストあり)。
 * backend/services/swipe_clip/params.py は上限値と尺の計算だけを同じ規則で持つ。
 * どれかを変えたら必ず全部を揃える。
 *
 * このファイルは依存を zod だけに留め、Node / ブラウザのどちらでも読めるようにする。
 */
import { z } from 'zod';

// ============================================================
// 上限・既定値 (§9。FE / Flask / render-service で同じ値)
// ============================================================

export const SWIPE_FPS = 30;
/** 生成尺の下限・上限 (秒)。 */
export const SWIPE_MIN_TOTAL_SEC = 1.0;
export const SWIPE_MAX_TOTAL_SEC = 30.0;
/** スワイプ回数の上限 (0 = 静止のみ)。 */
export const SWIPE_COUNT_MAX = 12;
/** 1 回のスワイプ量 (画面の高さに対する %) の範囲。 */
export const SWIPE_AMOUNT_PCT_MIN = 10;
export const SWIPE_AMOUNT_PCT_MAX = 100;
/** 最初 / 最後の静止 (秒)。 */
export const SWIPE_HOLD_SEC_MAX = 10;
/** 1 回のスワイプの長さ (秒)。fitScene で縮めるときの下限も SWIPE_SEC_MIN。
 *  上限は最大尺と同じ (2026-10-13 に 2 秒から緩和): 上から下まで 1 回でゆっくり見せる用途のため。実際の上限は
 *  合計 ≤ SWIPE_MAX_TOTAL_SEC の検証 (最初 / 最後 / 間の静止を除いた残り) で決まり、UI はその値を入力の max にする */
export const SWIPE_SEC_MIN = 0.2;
export const SWIPE_SEC_MAX = SWIPE_MAX_TOTAL_SEC;
/** スワイプの間の静止 (秒)。 */
export const SWIPE_GAP_SEC_MAX = 5;
/** 元画像の上限 (§9)。 */
export const SWIPE_IMAGE_MAX_WIDTH = 8192;
export const SWIPE_IMAGE_MAX_HEIGHT = 32000;
export const SWIPE_IMAGE_MAX_PIXELS = 120_000_000;
/** 切り詰め後の高さの下限 (px)。0px / 1px の画像を作らない (§5.4)。 */
export const SWIPE_CROP_MIN_PX = 16;
/** 「少し戻る」(ease-out-back) の係数。実機で決める暫定値 (§13-2)。 */
export const SWIPE_BOUNCE_C = 1.2;
/** 描画時の拡大率がこれを超えると UI が警告する (§4.2)。 */
export const SWIPE_UPSCALE_WARN_RATIO = 1.3;

export const SWIPE_DIRECTIONS = ['down', 'up'] as const;
export type SwipeDirection = (typeof SWIPE_DIRECTIONS)[number];
/** 手触り: settle = すっと止まる (ease-out) / bounce = 少し戻る (ease-out-back) / smooth = なめらか (ease-in-out) / linear = 一定速度 */
export const SWIPE_FEELS = ['settle', 'bounce', 'smooth', 'linear'] as const;
export type SwipeFeel = (typeof SWIPE_FEELS)[number];
export const SWIPE_TIMING_MODES = ['explicit', 'fitScene'] as const;
export type SwipeTimingMode = (typeof SWIPE_TIMING_MODES)[number];
/** 枠: none = 無し (画面 = 出力全面) / phone = スマホ風の筐体 / pc = ノート PC 風の筐体。どの縦横の出力でも選べる (§11.1)。
 *  筐体の寸法は geometry.ts の frameLayoutFor が正本 */
export const SWIPE_FRAMES = ['none', 'phone', 'pc'] as const;
export type SwipeFrame = (typeof SWIPE_FRAMES)[number];
/** 枠の位置 (2026-10-13): 横長の出力では start = 左 / end = 右、縦長では start = 上 / end = 下 (正方形は横長扱い)。省略 = center。
 *  端からの余白は出力の短辺の 12% (geometry.ts の frameEdgeMargin)。none の枠では無視 */
export const SWIPE_FRAME_POSITIONS = ['start', 'center', 'end'] as const;
export type SwipeFramePosition = (typeof SWIPE_FRAME_POSITIONS)[number];

// ============================================================
// 保存形 (SwipeClipParams v1・常に元画像基準・§5.2)
// ============================================================

const pct = z.number().min(0).max(100);

export const SwipeSourceSchema = z.object({
  materialId: z.string().min(1),
  folder: z.enum(['temporary', 'shared']),
  /** 元画像 (切り詰め前) の実寸。BE が DB の値で上書きする。 */
  width: z.number().int().positive(),
  height: z.number().int().positive(),
});
export type SwipeSource = z.infer<typeof SwipeSourceSchema>;

export const SwipeOutputSchema = z.object({
  width: z.number().int().positive(),
  height: z.number().int().positive(),
  fps: z.literal(SWIPE_FPS),
  aspectRatio: z.string().min(1),
});
export type SwipeOutput = z.infer<typeof SwipeOutputSchema>;

export const SwipeRangeSchema = z
  .object({
    /** 最初に画面の上端が来る位置 (元画像の高さ %)。 */
    startPct: pct,
    /** 最後に画面の下端が来る位置 (元画像の高さ %)。 */
    endPct: pct,
  })
  .refine((r) => r.startPct < r.endPct, { message: 'range.startPct は endPct より小さくしてください' });
export type SwipeRange = z.infer<typeof SwipeRangeSchema>;

export const SwipeSwipesSchema = z.object({
  count: z.number().int().min(0).max(SWIPE_COUNT_MAX),
  /** 1 回の移動量 = 画面の高さに対する %。省略 = 均等割。 */
  amountPct: z.number().min(SWIPE_AMOUNT_PCT_MIN).max(SWIPE_AMOUNT_PCT_MAX).optional(),
  direction: z.enum(SWIPE_DIRECTIONS),
  feel: z.enum(SWIPE_FEELS),
});
export type SwipeSwipes = z.infer<typeof SwipeSwipesSchema>;

export const SwipeTimingSchema = z.object({
  mode: z.enum(SWIPE_TIMING_MODES),
  holdStartSec: z.number().min(0).max(SWIPE_HOLD_SEC_MAX),
  holdEndSec: z.number().min(0).max(SWIPE_HOLD_SEC_MAX),
  swipeSec: z.number().min(SWIPE_SEC_MIN).max(SWIPE_SEC_MAX),
  gapSec: z.number().min(0).max(SWIPE_GAP_SEC_MAX),
  /** fitScene のときのシーン尺 (秒)。BE が保存時に確定する。 */
  totalSec: z.number().positive().max(SWIPE_MAX_TOTAL_SEC).optional(),
});
export type SwipeTiming = z.infer<typeof SwipeTimingSchema>;

export const SwipeLookSchema = z.object({
  /** '#RRGGBB' */
  backgroundColor: z.string().regex(/^#[0-9A-Fa-f]{6}$/),
  frame: z.enum(SWIPE_FRAMES),
  /** 枠の位置。省略 = center (旧保存形との互換)。 */
  framePosition: z.enum(SWIPE_FRAME_POSITIONS).optional(),
  /** 背景画像 (素材・2026-10-14)。出力全面に cover で敷き、その上に筐体と画面を重ねる。省略 = 背景色のみ。
   *  描画に使う URL は保存形には入れず、SwipeClip の props (backgroundSrc) で渡す */
  backgroundImage: z.object({ materialId: z.string().min(1), folder: z.enum(['temporary', 'shared']) }).optional(),
  /** 背景画像をぼかすか (2026-10-14)。省略 = false。強さは固定 (出力の短辺の 2%・SwipeClip.tsx の backgroundBlurPx) */
  backgroundBlur: z.boolean().optional(),
});
export type SwipeLook = z.infer<typeof SwipeLookSchema>;

export const SwipeClipParamsSchema = z.object({
  version: z.literal(1),
  source: SwipeSourceSchema,
  output: SwipeOutputSchema,
  range: SwipeRangeSchema,
  swipes: SwipeSwipesSchema,
  timing: SwipeTimingSchema,
  look: SwipeLookSchema,
  /** 入力 (チェック状態)。生成結果の素材 id は保存形に入れない (§5.4 / §7.3)。 */
  cropToEnd: z.object({ enabled: z.boolean() }),
});
export type SwipeClipParams = z.infer<typeof SwipeClipParamsSchema>;

/** 新規作成時の既定値 (source / output は呼び出し側が埋める)。 */
export const SWIPE_DEFAULTS = {
  range: { startPct: 0, endPct: 100 } satisfies SwipeRange,
  // 既定は「画面の割合で」(1 回に画面の 80%・本物のフリックに近い)。終了地点は上限として働く
  swipes: { count: 2, amountPct: 80, direction: 'down', feel: 'settle' } satisfies SwipeSwipes,
  // 既定は「シーンの尺に合わせる」(totalSec は呼び出し側がシーン尺で埋める。最初 / 最後の静止は差を吸収する比率)
  timing: { mode: 'fitScene', holdStartSec: 0.8, holdEndSec: 1.0, swipeSec: 0.6, gapSec: 0.8 } satisfies SwipeTiming,
  look: { backgroundColor: '#FFFFFF', frame: 'none' } satisfies SwipeLook,
  cropToEnd: { enabled: false },
} as const;

// ============================================================
// レンダ入力 (SwipeRenderInput・§5.5-5 / §6.1)
// ============================================================

export const SwipeSizeSchema = z.object({
  width: z.number().int().positive(),
  height: z.number().int().positive(),
});
export type SwipeSize = z.infer<typeof SwipeSizeSchema>;

export const SwipeRectSchema = z.object({
  x: z.number().int().min(0),
  y: z.number().int().min(0),
  width: z.number().int().positive(),
  height: z.number().int().positive(),
});
export type SwipeRect = z.infer<typeof SwipeRectSchema>;

export const SwipeRenderInputSchema = z.object({
  /** `src` として渡す画像の実寸。VE (レンダ) は加工済み画像なので image == virtual、FE は元画像。 */
  image: SwipeSizeSchema,
  /** image の座標系での切り詰め後の高さ。VE は image.height と同じ、FE は H'。 */
  crop: z.object({ height: z.number().int().positive() }),
  /** 切り詰め + 縮小後の整数寸法。停止位置・viewportPct の基準。 */
  virtual: SwipeSizeSchema,
  output: SwipeOutputSchema,
  /** スクリーン矩形 (v1 は出力全面)。 */
  screenRect: SwipeRectSchema,
  screenRadius: z.number().min(0),
  /** virtual 基準の開始位置 (画面上端) と終了地点 (画面下端)。 */
  s0: pct,
  sEndEdge: pct,
  swipes: SwipeSwipesSchema,
  timing: SwipeTimingSchema,
  look: SwipeLookSchema,
});
export type SwipeRenderInput = z.infer<typeof SwipeRenderInputSchema>;

export function validateSwipeClipParams(raw: unknown): SwipeClipParams {
  return SwipeClipParamsSchema.parse(raw);
}

export function validateSwipeRenderInput(raw: unknown): SwipeRenderInput {
  const input = SwipeRenderInputSchema.parse(raw);
  if (input.crop.height > input.image.height) {
    throw new Error('crop.height は image.height 以下にしてください');
  }
  if (input.s0 >= input.sEndEdge) {
    throw new Error('s0 は sEndEdge より小さくしてください');
  }
  return input;
}
